import React, { useEffect } from 'react'
import { usePaymentPlanAdvancePayments } from '@/v2/hooks/usePatientFinance'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import ActionButton from '@/v2/components/common/ActionButton'
import Label from '@/v2/components/common/FieldLabel'
import Typography from '@/v2/components/common/Typography'
import SelectPaymentCardField from '@/v2/components/common/SelectPaymentCardField'
import SelectPaymentAccountField from '@/v2/components/common/SelectPaymentAccountField'
import ControlRadioField from '@/v2/components/common/ControlRadioField'
import CurrencyInputField from '@/v2/components/common/CurrencyInputField'
import InputField from '@/v2/components/common/InputField'
import FormControl from '@mui/material/FormControl'
import Divider from '@/v2/components/common/Divider'
import { useForm } from 'react-hook-form'
import { fromCurrencyToNumber, toCurrency } from '@/v2/utils/helpers'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

const schema = yup
  .object({
    paymentPlanId: yup.string().required('Payment plan is required'),
    paymentMethod: yup
      .string()
      .oneOf(['cash', 'credit_card', 'check', 'ach'])
      .required('Payment method is required'),
    paymentMethodId: yup.string().notRequired(),
    amountTargeted: yup
      .number()
      .transform((value, originalValue) => {
        // parse the currency-string into a number
        if (typeof originalValue === 'string') {
          return fromCurrencyToNumber(originalValue)
        }
        return value
      })
      .typeError('Amount must be a number')
      .min(0.5, 'Amount must be greater than $0.50')
      .required('Amount is required'),
    additionalInfo: yup.string().when('paymentMethod', {
      is: 'check',
      then: s =>
        s.required(
          'Additional payment info is required for this payment method'
        ),
      otherwise: s => s.notRequired(),
    }),
  })
  .noUnknown()

const AdvancedPaymentForm = ({
  plan,
  onClose,
  patientId,
  creditCards,
  bankAccounts,
}) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const {
    createAdvancePayment,
    isCreatingAdvancePayment,
  } = usePaymentPlanAdvancePayments({
    patientId,
    paymentPlanId: plan?.id,
  })

  const hasPaymentMethods = creditCards?.length > 0
  const hasBankAccounts = bankAccounts?.length > 0
  const hasOnlyOnePaymentMethod = creditCards?.length === 1

  const defaultMethod = hasOnlyOnePaymentMethod
    ? 'credit_card'
    : hasPaymentMethods
    ? 'credit_card'
    : hasBankAccounts
    ? 'ach'
    : 'cash'

  const defaultMethodId = hasOnlyOnePaymentMethod ? creditCards[0].id : null

  const {
    control,
    register,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    handleSubmit: handleSubmitForm,
    watch,
  } = useForm({
    defaultValues: {
      paymentPlanId: plan?.id || '',
      paymentMethod: defaultMethod,
      paymentMethodId: defaultMethodId,
      amountTargeted: '',
      additionalInfo: '',
    },
    resolver: yupResolver(schema),
  })

  const watchMethod = watch('paymentMethod')

  const onSubmit = async data => {
    try {
      const isStripe =
        data.paymentMethod === 'credit_card' || data.paymentMethod === 'ach'
      await createAdvancePayment({
        advancePayment: {
          amount: Number(data.amountTargeted),
          payment: {
            paymentMethod: data.paymentMethod,
            gateway: isStripe ? 'stripe' : 'on_site',
            paymentMethodId: isStripe ? data.paymentMethodId : null,
            additionalInfo: data.additionalInfo,
          },
        },
      })
    } catch (e) {
      setError('root.changePaymentMethod', {
        type: 'manual',
        message: e.message,
      })
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    handleSubmitForm(onSubmit)()
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      onClose()
      onOpenSnackbar('Payment plan updated successfully')
    }
  }, [isSubmitSuccessful, onClose, onOpenSnackbar])

  const paymentMethodOptions = [
    { label: 'Cash', value: 'cash' },
    { label: 'Check', value: 'check' },
  ]
  if (hasPaymentMethods)
    paymentMethodOptions.unshift({ label: 'Credit Card', value: 'credit_card' })
  if (hasBankAccounts) paymentMethodOptions.push({ label: 'ACH', value: 'ach' })

  return (
    <FormControl component="form" onSubmit={handleSubmit} variant="filled">
      {errors.root?.changePaymentMethod && (
        <Box bgcolor="error.dark" color="common.white" p={1} mb={2}>
          {errors.root.changePaymentMethod.message}
        </Box>
      )}

      {plan && (
        <>
          <Box display="flex" gap={1} mb={1}>
            <Typography fontSize="14px">Remaining Balance:</Typography>
            <Typography bold fontSize="14px">
              ${toCurrency(plan.remainingPayable)}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      <CurrencyInputField
        label="Amount"
        name="amountTargeted"
        placeholder="$0.00"
        prefix="$"
        error={errors.amountTargeted?.message}
        control={control}
      />

      <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
        <Label>Payment Method</Label>
        <ControlRadioField
          id="paymentMethod"
          options={paymentMethodOptions}
          color="secondary"
          column
          property="value"
          name="paymentMethod"
          control={control}
        />
      </FormControl>

      <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
        {watchMethod === 'credit_card' && <Label>Select a credit card</Label>}
        {watchMethod === 'ach' && <Label>Select a bank account</Label>}
        {watchMethod === 'check' && <Label>Additional Info</Label>}

        {watchMethod === 'credit_card' && (
          <SelectPaymentCardField
            control={control}
            name="paymentMethodId"
            paymentMethods={creditCards}
            error={errors.paymentMethodId}
          />
        )}
        {watchMethod === 'ach' && (
          <SelectPaymentAccountField
            control={control}
            name="paymentMethodId"
            paymentMethods={bankAccounts}
            error={errors.paymentMethodId}
          />
        )}
        {watchMethod === 'check' && (
          <InputField
            {...register('additionalInfo')}
            error={errors.additionalInfo?.message}
            fullWidth
            multiline
            label="Additional info"
          />
        )}
      </FormControl>

      <Grid container justifyContent="flex-end">
        <ActionButton
          variant="contained"
          color="secondary"
          type="submit"
          disabled={isSubmitting || isCreatingAdvancePayment}
          loading={isSubmitting || isCreatingAdvancePayment}
        >
          {['cash', 'check'].includes(watchMethod)
            ? 'Mark as Paid'
            : 'Charge Now'}
        </ActionButton>
      </Grid>
    </FormControl>
  )
}

export default AdvancedPaymentForm
